import {
  Injectable,
  inject,
  signal,
  computed,
  effect,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import {
  Firestore,
  doc,
  collection,
  onSnapshot,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { AppUser } from '../models/user.model';
import {
  atLeast,
  BranchRole,
  BusinessRole,
  CallerAuthority,
  highestAuthority,
  isBranchRole,
} from '../models/role.model';
import {
  BusinessMembershipMirror,
  BranchMembershipMirror,
} from '../models/membership.model';

/**
 * Central access-control service replacing the old PermissionService.
 *
 * - Loads the current user's Firestore doc
 * - Loads mirrored membership subcollections (server-written, client read-only)
 * - Checks SuperAdmin via Firebase Auth custom claim (zero Firestore reads)
 * - Manages selected business/branch context
 */
@Injectable({ providedIn: 'root' })
export class AccessService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private injector = inject(Injector);

  currentUser = signal<AppUser | null>(null);
  loaded = signal(false);

  /** Parsed from Auth custom claim — NOT from Firestore */
  private superAdminClaim = signal(false);
  isSuperAdmin = computed(() => this.superAdminClaim());
  /** Token claims parsed; until then isSuperAdmin() is "not yet", not "no". */
  private claimsLoaded = signal(false);

  businessMemberships = signal<BusinessMembershipMirror[]>([]);
  branchMemberships = signal<BranchMembershipMirror[]>([]);

  private bizMirrorLoaded = signal(false);
  private branchMirrorLoaded = signal(false);

  /**
   * True once BOTH membership mirrors have delivered a snapshot.
   *
   * Distinct from `loaded`, which only tracks the user document. An empty
   * mirror is indistinguishable from one that has not arrived yet, so anything
   * deciding access from membership — every scope-based guard — must wait for
   * this or it will act on empty signals and wrongly deny.
   */
  mirrorsLoaded = computed(
    () => this.bizMirrorLoaded() && this.branchMirrorLoaded(),
  );

  /** Everything needed for an access decision is present. */
  accessReady = computed(() => this.loaded() && this.mirrorsLoaded());

  selectedBusinessId = signal<string | null>(null);
  selectedBranchId = signal<string | null>(null);

  private userUnsub: (() => void) | null = null;
  private bizMirrorUnsub: (() => void) | null = null;
  private branchMirrorUnsub: (() => void) | null = null;
  private userNotFoundTimeout: ReturnType<typeof setTimeout> | null = null;
  private initialContextApplied = false;

  constructor() {
    effect(() => {
      const firebaseUser = this.authService.user();
      this.cleanup();

      if (firebaseUser) {
        runInInjectionContext(this.injector, () => {
          // A rejected token read (offline, revoked) would otherwise be an
          // unhandled rejection and a silent SuperAdmin downgrade.
          void this.loadClaims().catch((err: unknown) =>
            console.error('Failed to load auth claims:', err),
          );
          this.subscribeToUser(firebaseUser.uid);
          this.subscribeToMirrors(firebaseUser.uid);
        });
      } else {
        this.currentUser.set(null);
        this.superAdminClaim.set(false);
        this.claimsLoaded.set(false);
        this.businessMemberships.set([]);
        this.branchMemberships.set([]);
        this.bizMirrorLoaded.set(false);
        this.branchMirrorLoaded.set(false);
        this.selectedBusinessId.set(null);
        this.selectedBranchId.set(null);
        this.initialContextApplied = false;
        this.loaded.set(false);
      }
    });

    // Drop a selection the mirrors no longer back. The last-selected context is
    // restored from users/{uid} before the mirrors arrive, so a user removed
    // from that business signed in with it selected and every screen failed
    // permission-denied; the same check catches a removal mid-session. Gated on
    // accessReady (and the claims) rather than loaded, because an empty mirror
    // on a cold load is indistinguishable from "not a member" and would wipe a
    // valid selection. SuperAdmin has no mirrors and may select anything. Each
    // correction is one self-write; the snapshot it triggers finds nothing left
    // to clear, so there is no loop.
    effect(() => {
      if (!this.accessReady() || !this.claimsLoaded() || this.isSuperAdmin()) {
        return;
      }
      const businessId = this.selectedBusinessId();
      if (businessId && !this.getScopedBusinessIds().includes(businessId)) {
        void this.selectBusiness(null);
        return;
      }
      const branchId = this.selectedBranchId();
      if (branchId && this.getBranchRole(branchId) === null) {
        void this.selectBranch(null);
      }
    });
  }

  // ── Public query helpers ─────────────────────────────────────────────────

  isActive(): boolean {
    return this.currentUser()?.status === 'ACTIVE';
  }

  getBusinessRole(businessId: string): BusinessRole | null {
    const m = this.businessMemberships().find(
      (bm) => bm.businessId === businessId && bm.status === 'ACTIVE',
    );
    return m?.role ?? null;
  }

  getBranchRole(branchId: string): BranchRole | null {
    const m = this.branchMemberships().find(
      (bm) => bm.branchId === branchId && bm.status === 'ACTIVE',
    );
    return m?.role ?? null;
  }

  getAccessibleBusinessIds(): string[] {
    return this.businessMemberships()
      .filter((m) => m.status === 'ACTIVE')
      .map((m) => m.businessId);
  }

  getAccessibleBranchIds(businessId?: string): string[] {
    return this.branchMemberships()
      .filter(
        (m) =>
          m.status === 'ACTIVE' &&
          (!businessId || m.businessId === businessId),
      )
      .map((m) => m.branchId);
  }

  // ── Scope and capability helpers ──────────────────────────────────────────
  //
  // These are METHODS, not computed signals, because they take arguments. They
  // read signals internally, so calling one inside a component `computed()`
  // still tracks correctly — do not "fix" them into signals.
  //
  // Every capability below is UI gating only. The authoritative check is in
  // firestore.rules, a callable, or task-api middleware; see the capability
  // matrix in docs/roles-and-rules.md. Hiding a button the server would refuse
  // is a courtesy, not a security boundary.

  /**
   * Business IDs the user has ANY standing in — a business membership, or a
   * branch membership somewhere within it.
   *
   * The client-side twin of users/{uid}/businessScopes/{bid}, derived from the
   * two mirrors we already subscribe to so it needs no extra read. Prefer this
   * over getAccessibleBusinessIds() anywhere a branch-only OWNER or MANAGER must
   * be able to see the business: that method reads only the *business* mirror,
   * which they have no entry in.
   */
  getScopedBusinessIds(): string[] {
    const ids = new Set(this.getAccessibleBusinessIds());
    for (const m of this.branchMemberships()) {
      if (m.status === 'ACTIVE' && m.businessId) ids.add(m.businessId);
    }
    return [...ids];
  }

  /**
   * The user's highest authority within one business.
   * SUPER_ADMIN > BUSINESS > OWNER > MANAGER, or null for no standing at all.
   */
  businessAuthority(businessId: string | null): CallerAuthority | null {
    if (this.isSuperAdmin()) return 'SUPER_ADMIN';
    if (!businessId) return null;

    const held: CallerAuthority[] = [];
    if (this.getBusinessRole(businessId) === 'BUSINESS') held.push('BUSINESS');
    for (const m of this.branchMemberships()) {
      if (m.status === 'ACTIVE' && m.businessId === businessId && isBranchRole(m.role)) {
        held.push(m.role);
      }
    }
    return highestAuthority(held);
  }

  /** The user's authority in one branch. */
  branchAuthority(branchId: string | null): CallerAuthority | null {
    if (this.isSuperAdmin()) return 'SUPER_ADMIN';
    if (!branchId) return null;
    return this.getBranchRole(branchId);
  }

  hasAnyBusinessScope(): boolean {
    return this.isSuperAdmin() || this.getScopedBusinessIds().length > 0;
  }

  /**
   * Guarantee a selected business where one can be inferred, and report whether
   * there now is one.
   *
   * A freshly invited user has no `lastSelectedBusinessId`, so nothing is
   * selected until the context selector's auto-select effect runs — and that is
   * a component, which may not have rendered when a route guard asks. Deep
   * links to business-scoped screens would bounce to the dashboard on the first
   * navigation after signing in.
   *
   * Only auto-selects when the choice is unambiguous. With several businesses
   * the user genuinely has to pick.
   */
  ensureBusinessContext(): boolean {
    if (this.selectedBusinessId() !== null) return true;
    const scoped = this.getScopedBusinessIds();
    if (scoped.length === 1 && scoped[0]) {
      void this.selectBusiness(scoped[0]);
      return true;
    }
    // SuperAdmin belongs to no business, so there is nothing to infer; they
    // pick from the full list in the selector.
    return false;
  }

  /**
   * A DISABLED user's UI collapses entirely. This is the UI half only: the
   * rules half is `isActiveUser()` in firestore.rules, which gates every
   * resource path (the self profile read and the user's own mirrors are the
   * two deliberate skips, so authGuard can discover the status and sign out),
   * and the API's `checkRevoked` covers the Task API.
   */
  private canAct(): boolean {
    return this.isSuperAdmin() || this.isActive();
  }

  canViewForms(businessId: string | null): boolean {
    return this.canAct() && this.businessAuthority(businessId) !== null;
  }

  canManageForms(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  canManageBranches(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  canManageBusinessMembers(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  canAppointOwner(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  /** SuperAdmin, a BUSINESS admin of the parent business, or the branch's OWNER. */
  canManageBranchMembers(branchId: string | null): boolean {
    if (!this.canAct() || !branchId) return false;
    const mirror = this.branchMemberships().find(
      (m) => m.branchId === branchId && m.status === 'ACTIVE',
    );
    if (this.canManageBusinessMembers(mirror?.businessId ?? null)) return true;
    return this.branchAuthority(branchId) === 'OWNER';
  }

  /**
   * Whether the user may act on one specific branch member.
   *
   * A `BUSINESS` target is refused for everyone: that document is a seeded copy
   * of the business membership, maintained by the business-level sweeps in the
   * manageMembership callable. Mutating it at branch scope desyncs the
   * auto-seeding invariant that every one-read branch rule depends on.
   */
  canManageBranchMember(
    branchId: string | null,
    targetRole: BranchRole,
  ): boolean {
    if (targetRole === 'BUSINESS') return false;
    if (!this.canManageBranchMembers(branchId)) return false;
    // A bare OWNER may only act on MANAGERs; business admins may act on either.
    if (this.branchAuthority(branchId) === 'OWNER') {
      const mirror = this.branchMemberships().find(
        (m) => m.branchId === branchId && m.status === 'ACTIVE',
      );
      if (!this.canManageBusinessMembers(mirror?.businessId ?? null)) {
        return targetRole === 'MANAGER';
      }
    }
    return true;
  }

  canAssignToWholeBusiness(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  /**
   * OWNER or above in the branch. A MANAGER may complete work in a branch but
   * not schedule it.
   *
   * This used to admit any active member. Scheduling decides what a branch is
   * measured on, and an assignment's criticality band decides what a miss costs,
   * so whoever does the work must not also decide whether it counts. The API
   * enforces the same rule in `requireBranchOwner`; this only stops the UI
   * offering an action that would come back 403.
   */
  canAssignToBranch(branchId: string | null): boolean {
    return this.canAct() && atLeast(this.branchAuthority(branchId), 'OWNER');
  }

  /**
   * OWNER or above somewhere in this business.
   *
   * The dividing line between deciding what work happens and carrying it out.
   * Both Assignments and Reports sit on this side of it; Checklists does not.
   */
  hasOwnerScope(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'OWNER');
  }

  /**
   * Who may open the Compliance screen (the `/reports` route).
   *
   * Any branch role in this business, MANAGER included — a manager sees their
   * own branch's score, an OWNER the branches they hold, and a business admin
   * the whole business. That narrowing is the Task API's (`visibleBranchIds`);
   * this only decides whether the screen is offered. Read-only: nothing on the
   * screen changes what is scored.
   *
   * Written as `atLeast(…, 'MANAGER')` rather than "any standing" so a role
   * added below MANAGER is excluded until someone decides otherwise.
   */
  canViewReports(businessId: string | null): boolean {
    return (
      this.canAct() && atLeast(this.businessAuthority(businessId), 'MANAGER')
    );
  }

  /**
   * Who may read the history of settled work.
   *
   * The same line as the Compliance screen, because it is the evidence behind
   * that score and the API scopes both through one helper. Named separately from
   * canViewReports rather than reused at the call site, so the two can diverge
   * without a silent change of meaning.
   */
  canViewTaskHistory(businessId: string | null): boolean {
    return (
      this.canAct() && atLeast(this.businessAuthority(businessId), 'MANAGER')
    );
  }

  /**
   * Whether the whole-business roll-up and the branch-by-branch comparison are
   * visible, as opposed to only the caller's own branches. An OWNER's roll-up
   * covers the branches they hold and nothing else.
   */
  canViewBusinessWideReports(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  /** Others deactivate instead — deleting an assignment can orphan submissions. */
  canDeleteAssignments(businessId: string | null): boolean {
    return this.canAct() && atLeast(this.businessAuthority(businessId), 'BUSINESS');
  }

  /** Hard Auth-account deletion is SuperAdmin-only and stays that way. */
  canHardDeleteUsers(): boolean {
    return this.isSuperAdmin();
  }

  // ── Context selection ─────────────────────────────────────────────────────

  async selectBusiness(businessId: string | null): Promise<void> {
    this.selectedBusinessId.set(businessId);
    this.selectedBranchId.set(null);
    const uid = this.currentUser()?.uid;
    if (uid) {
      await this.userService.updateUser(uid, {
        lastSelectedBusinessId: businessId ?? undefined,
        lastSelectedBranchId: undefined,
      });
    }
  }

  async selectBranch(branchId: string | null): Promise<void> {
    this.selectedBranchId.set(branchId);
    const uid = this.currentUser()?.uid;
    if (uid) {
      await this.userService.updateUser(uid, {
        lastSelectedBranchId: branchId ?? undefined,
      });
    }
  }

  // ── Private subscriptions ────────────────────────────────────────────────

  private async loadClaims(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;
    const result = await user.getIdTokenResult();
    this.superAdminClaim.set(result.claims['superAdmin'] === true);
    this.claimsLoaded.set(true);
  }

  private subscribeToUser(uid: string): void {
    const userRef = doc(this.firestore, 'users', uid);

    this.userUnsub = runInInjectionContext(this.injector, () =>
      onSnapshot(
        userRef,
        (snap) => {
          if (snap.exists()) {
            this.clearUserNotFoundTimeout();
            const userData = { uid: snap.id, ...snap.data() } as AppUser;
            this.currentUser.set(userData);

            if (!this.initialContextApplied) {
              this.initialContextApplied = true;
              if (userData.lastSelectedBusinessId) {
                this.selectedBusinessId.set(userData.lastSelectedBusinessId);
              }
              if (userData.lastSelectedBranchId) {
                this.selectedBranchId.set(userData.lastSelectedBranchId);
              }
            }
          } else {
            this.currentUser.set(null);
            this.clearUserNotFoundTimeout();
            this.userNotFoundTimeout = setTimeout(() => {
              this.userNotFoundTimeout = null;
              this.loaded.set(true);
            }, 2000);
            return;
          }
          this.loaded.set(true);
        },
        (err) => {
          console.error('Failed to load user document:', err);
          this.currentUser.set(null);
          this.loaded.set(true);
        },
      ),
    );
  }

  private subscribeToMirrors(uid: string): void {
    const bizRef = collection(this.firestore, `users/${uid}/businessMemberships`);
    this.bizMirrorUnsub = runInInjectionContext(this.injector, () =>
      onSnapshot(
        bizRef,
        (snap) => {
          this.businessMemberships.set(
            snap.docs.map(
              (d) => ({ businessId: d.id, ...d.data() }) as BusinessMembershipMirror,
            ),
          );
          this.bizMirrorLoaded.set(true);
        },
        // A denied or failed listener must still release the gate, or every
        // scope guard waits forever and the app appears to hang rather than
        // showing an error.
        (err) => {
          console.error('Business membership mirror failed:', err);
          this.bizMirrorLoaded.set(true);
        },
      ),
    );

    const brRef = collection(this.firestore, `users/${uid}/branchMemberships`);
    this.branchMirrorUnsub = runInInjectionContext(this.injector, () =>
      onSnapshot(
        brRef,
        (snap) => {
          this.branchMemberships.set(
            snap.docs.map(
              (d) => ({ branchId: d.id, ...d.data() }) as BranchMembershipMirror,
            ),
          );
          this.branchMirrorLoaded.set(true);
        },
        (err) => {
          console.error('Branch membership mirror failed:', err);
          this.branchMirrorLoaded.set(true);
        },
      ),
    );
  }

  private clearUserNotFoundTimeout(): void {
    if (this.userNotFoundTimeout) {
      clearTimeout(this.userNotFoundTimeout);
      this.userNotFoundTimeout = null;
    }
  }

  private cleanup(): void {
    this.clearUserNotFoundTimeout();
    this.initialContextApplied = false;
    if (this.userUnsub) { this.userUnsub(); this.userUnsub = null; }
    if (this.bizMirrorUnsub) { this.bizMirrorUnsub(); this.bizMirrorUnsub = null; }
    if (this.branchMirrorUnsub) { this.branchMirrorUnsub(); this.branchMirrorUnsub = null; }
  }
}
